import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { constants } from "@lib/constants";

const initialState = {
  allJobs: [],
  allJobsLoading: false,

  jobDetails: null,
  jobLoading: false,

  savedJobs: [],
  savedJobsLoading: false,

  submittedJobs: [],
  submittedJobsLoading: false,

  shortlistedJobs: [],
  shortlistedJobsLoading: false,

  // 🔹 NEW: Draft Jobs
  draftJobs: [],
  draftJobsLoading: false,

  error: null,
};

/* ----------------------------------------------
   🔹 Fetch Draft Jobs
------------------------------------------------*/
export const fetchDraftJobs = createAsyncThunk(
  "jobReducer/fetchDraftJobs",
  async ({ axiosGet }, thunkAPI) => {
    try {
      const res = await axiosGet(`job/drafts`);
      return res?.data?.records || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/* ----------------------------------------------
   🔹 Fetch all jobs
------------------------------------------------*/
export const fetchJobs = createAsyncThunk(
  "jobReducer/fetchJobs",
  async ({ axiosGet, userId, params }, thunkAPI) => {
    try {
      const response = await axiosGet(
        `${constants.LIST_JOBS}/${userId}`,
        params,
      );
      return response.data.records;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/* ----------------------------------------------
   🔹 Fetch job details
------------------------------------------------*/
export const fetchJobDetails = createAsyncThunk(
  "jobReducer/fetchJobDetails",
  async ({ axiosGet, id }, thunkAPI) => {
    try {
      const response = await axiosGet(`${constants.GET_JOB}/${id}`);
      return response.data.records;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/* ----------------------------------------------
   🔹 Fetch saved jobs
------------------------------------------------*/
export const fetchSavedJobs = createAsyncThunk(
  "jobReducer/fetchSavedJobs",
  async ({ axiosGet }, thunkAPI) => {
    try {
      const res = await axiosGet(`job/saved`);
      return res?.data?.records || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/* ----------------------------------------------
   🔹 Fetch submitted jobs
------------------------------------------------*/
export const fetchSubmittedJobs = createAsyncThunk(
  "jobReducer/fetchSubmittedJobs",
  async ({ axiosGet }, thunkAPI) => {
    try {
      const res = await axiosGet(`job/applications`);
      return res?.data?.applications || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

export const fetchShortlistedJobs = createAsyncThunk(
  "jobReducer/fetchShortlistedJobs",
  async ({ axiosGet }, thunkAPI) => {
    try {
      const res = await axiosGet(`job/applications?status=shortlisted`);

      return res?.data?.applications || [];
    } catch (error) {
      return thunkAPI.rejectWithValue(error.message);
    }
  },
);

/* ----------------------------------------------
   🔹 Slice
------------------------------------------------*/
const jobReducer = createSlice({
  name: "jobReducer",
  initialState,
  reducers: {
    clearJobDetails: (state) => {
      state.jobDetails = null;
    },
  },

  extraReducers: (builder) => {
    builder
      /* -------------------------------
         🔹 Fetch Jobs
      --------------------------------*/
      .addCase(fetchJobs.pending, (state) => {
        state.allJobsLoading = true;
        state.error = null;
      })
      .addCase(fetchJobs.fulfilled, (state, action) => {
        console.log("JOBS API FULFILLED");
        state.allJobs = action.payload;
        state.allJobsLoading = false;
      })
      .addCase(fetchJobs.rejected, (state, action) => {
        state.allJobsLoading = false;
        state.error = action.payload;
      })

      /* -------------------------------
         🔹 Job Details
      --------------------------------*/
      .addCase(fetchJobDetails.pending, (state) => {
        state.jobLoading = true;
        state.error = null;
      })
      .addCase(fetchJobDetails.fulfilled, (state, action) => {
        state.jobDetails = action.payload;
        state.jobLoading = false;
      })
      .addCase(fetchJobDetails.rejected, (state, action) => {
        state.jobLoading = false;
        state.error = action.payload;
      })

      /* -------------------------------
         🔹 Saved Jobs
      --------------------------------*/
      .addCase(fetchSavedJobs.pending, (state) => {
        state.savedJobsLoading = true;
        state.error = null;
      })
      .addCase(fetchSavedJobs.fulfilled, (state, action) => {
        state.savedJobs = action.payload;
        state.savedJobsLoading = false;
      })
      .addCase(fetchSavedJobs.rejected, (state, action) => {
        state.savedJobsLoading = false;
        state.error = action.payload;
      })

      /* -------------------------------
         🔹 Submitted Jobs
      --------------------------------*/
      .addCase(fetchSubmittedJobs.pending, (state) => {
        state.submittedJobsLoading = true;
        state.error = null;
      })
      .addCase(fetchSubmittedJobs.fulfilled, (state, action) => {
        state.submittedJobs = action.payload;
        state.submittedJobsLoading = false;
      })
      .addCase(fetchSubmittedJobs.rejected, (state, action) => {
        state.submittedJobsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchShortlistedJobs.pending, (state) => {
        state.shortlistedJobsLoading = true;
        state.error = null;
      })
      .addCase(fetchShortlistedJobs.fulfilled, (state, action) => {
        state.shortlistedJobs = action.payload;
        state.shortlistedJobsLoading = false;
      })
      .addCase(fetchShortlistedJobs.rejected, (state, action) => {
        state.shortlistedJobsLoading = false;
        state.error = action.payload;
      })

      /* -------------------------------
         🔹 NEW: Draft Jobs
      --------------------------------*/
      .addCase(fetchDraftJobs.pending, (state) => {
        state.draftJobsLoading = true;
        state.error = null;
      })
      .addCase(fetchDraftJobs.fulfilled, (state, action) => {
        state.draftJobs = action.payload;
        state.draftJobsLoading = false;
      })
      .addCase(fetchDraftJobs.rejected, (state, action) => {
        state.draftJobsLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearJobDetails } = jobReducer.actions;
export default jobReducer.reducer;
